export type ProjectCategory =
  | 'Building & Facility Maintenance'
  | 'Residential Remodeling'
  | 'Commercial Remodeling'
  | 'General Maintenance';

export interface ProjectStat {
  label: string;
  value: string;
}

export interface Project {
  id: number;
  title: string;
  category: ProjectCategory;
  location: string;
  year: number;
  mainImage: string;
  gallery: string[];
  description: string;
  scope: string[];
  stats: ProjectStat[];
}

export const PORTFOLIO_PROJECTS: Project[] = [
  {
    id: 1,
    title: 'Westside Office Complex — Facade Restoration',
    category: 'Building & Facility Maintenance',
    location: 'Norcross, GA',
    year: 2024,
    mainImage:
      'https://public.readdy.ai/ai/img_res/b63010db31dab88e8b95c376e45cf606.jpg',
    gallery: [
      'https://readdy.ai/api/search-image?query=close-up%20detail%20of%20commercial%20concrete%20wall%20after%20professional%20repair%2C%20patched%20surface%20with%20fresh%20sealant%2C%20realistic%20construction%20site%20documentation%20photo%2C%20natural%20light%2C%20visible%20texture%20and%20material%20honesty%2C%20no%20people%2C%20documentary%20photography%20style&width=900&height=600&seq=bfm-r002&orientation=landscape',
      'https://readdy.ai/api/search-image?query=commercial%20building%20cladding%20panel%20replacement%20in%20progress%2C%20aluminum%20exterior%20facade%20installation%2C%20scaffolding%20and%20completed%20sections%2C%20real%20construction%20documentation%20photography%2C%20overcast%20natural%20lighting%2C%20no%20workers%20visible%2C%20honest%20project%20photo&width=900&height=600&seq=bfm-r003&orientation=landscape',
      'https://readdy.ai/api/search-image?query=building%20expansion%20joint%20sealant%20detail%20close-up%2C%20freshly%20applied%20weatherproof%20caulk%20on%20commercial%20building%2C%20construction%20quality%20detail%20photography%2C%20macro%20focus%2C%20natural%20light%2C%20realistic%20texture%2C%20no%20people%2C%20maintenance%20documentation%20shot&width=900&height=600&seq=bfm-r004&orientation=landscape',
    ],
    description:
      'Complete facade restoration for a 6-story commercial office complex in Norcross. The project included full exterior concrete repair, masonry tuckpointing, waterproof sealant application, and caulking replacement across all building joints — restoring structural integrity and preventing water intrusion.',
    scope: [
      'Concrete spalling repair & patching',
      'Full masonry tuckpointing',
      'Elastomeric waterproof coating',
      'Expansion joint sealant replacement',
      'Window perimeter caulking',
      'Final pressure wash & inspection',
    ],
    stats: [
      { label: 'Duration', value: '6 Weeks' },
      { label: 'Stories', value: '6' },
      { label: 'Area', value: '18,000 sq ft' },
      { label: 'Completion', value: 'Q2 2024' },
    ],
  },
  {
    id: 2,
    title: 'Summit Industrial Park — Structural Repair Program',
    category: 'Building & Facility Maintenance',
    location: 'Marietta, GA',
    year: 2024,
    mainImage:
      'https://readdy.ai/api/search-image?query=industrial%20warehouse%20exterior%20tilt-up%20concrete%20building%20after%20structural%20repair%2C%20realistic%20documentary%20photo%2C%20wide%20angle%20view%2C%20overcast%20sky%2C%20slight%20staining%20on%20concrete%20panels%20showing%20age%2C%20freshly%20patched%20sections%20visible%2C%20honest%20construction%20photography%2C%20no%20people%2C%20muted%20industrial%20palette&width=700&height=500&seq=bfm-r005&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=tilt-up%20concrete%20panel%20crack%20repair%20close-up%2C%20epoxy%20injection%20ports%20installed%2C%20before%20and%20after%20surface%20patching%2C%20realistic%20construction%20documentation%2C%20natural%20light%2C%20slightly%20dusty%20environment%2C%20no%20people%2C%20honest%20maintenance%20photography&width=900&height=600&seq=bfm-r006&orientation=landscape',
      'https://readdy.ai/api/search-image?query=flat%20commercial%20rooftop%20with%20new%20EPDM%20membrane%20installation%2C%20rolled%20roofing%20material%20with%20heat%20welded%20seams%2C%20overcast%20sky%20above%2C%20realistic%20roofing%20project%20documentation%20photo%2C%20honest%20slightly%20imperfect%20real%20work%2C%20no%20people&width=900&height=600&seq=bfm-r007&orientation=landscape',
      'https://readdy.ai/api/search-image?query=commercial%20loading%20dock%20concrete%20apron%20repair%2C%20freshly%20poured%20and%20finished%20concrete%20slab%2C%20dock%20leveler%20visible%2C%20industrial%20facility%20exterior%2C%20documentary%20construction%20photography%2C%20slightly%20worn%20surrounding%20area%20showing%20realistic%20context%2C%20no%20people&width=900&height=600&seq=bfm-r008&orientation=landscape',
    ],
    description:
      'Multi-phase structural repair program for a 4-building industrial park in Marietta. Work included tilt-up panel crack repairs, full roof membrane replacement on two buildings, dock area concrete restoration, and a comprehensive preventive maintenance plan going forward.',
    scope: [
      'Tilt-up wall panel crack injection',
      'Roof membrane full replacement (2 bldgs)',
      'Loading dock concrete apron repair',
      'Structural column base repair',
      'Facility-wide preventive inspection',
      'Ongoing maintenance contract',
    ],
    stats: [
      { label: 'Duration', value: '10 Weeks' },
      { label: 'Buildings', value: '4' },
      { label: 'Area', value: '62,000 sq ft' },
      { label: 'Completion', value: 'Q3 2024' },
    ],
  },
  {
    id: 3,
    title: 'Peachtree Towers — Waterproofing & Exterior Overhaul',
    category: 'Building & Facility Maintenance',
    location: 'Decatur, GA',
    year: 2024,
    mainImage:
      'https://readdy.ai/api/search-image?query=mid-rise%20residential%20apartment%20tower%20exterior%20freshly%20repainted%20and%20waterproofed%2C%20realistic%20documentary%20architectural%20photo%2C%20cloudy%20Atlanta%20sky%2C%20slightly%20weathered%20surroundings%20giving%20authentic%20context%2C%20new%20window%20caulking%20visible%2C%20no%20people%2C%20honest%20property%20maintenance%20result%20photography&width=700&height=500&seq=bfm-r009&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=concrete%20balcony%20deck%20with%20freshly%20applied%20traffic%20coating%2C%20slip-resistant%20surface%20texture%2C%20flashing%20detail%20at%20wall%20junction%2C%20natural%20daylight%2C%20realistic%20construction%20documentation%20photo%2C%20honest%20finish%20quality%2C%20no%20people&width=900&height=600&seq=bfm-r010&orientation=landscape',
      'https://readdy.ai/api/search-image?query=parking%20garage%20concrete%20deck%20after%20waterproofing%20restoration%2C%20new%20orange%20traffic%20markings%2C%20sealed%20joints%2C%20realistic%20documentary%20photo%20of%20finished%20commercial%20work%2C%20slightly%20imperfect%20authentic%20construction%20photography%2C%20no%20people&width=900&height=600&seq=bfm-r011&orientation=landscape',
      'https://readdy.ai/api/search-image?query=apartment%20building%20main%20entrance%20exterior%20renovation%20complete%2C%20freshly%20painted%20stone%20cladding%2C%20new%20caulking%20around%20windows%2C%20updated%20signage%2C%20realistic%20documentary%20architectural%20photography%2C%20natural%20overcast%20light%2C%20no%20people&width=900&height=600&seq=bfm-r012&orientation=landscape',
    ],
    description:
      'Comprehensive waterproofing and exterior overhaul for a 12-story residential tower in Decatur. Scope included full balcony deck coating replacement across 96 units, parking garage concrete deck restoration, window perimeter resealing, and a complete exterior repaint.',
    scope: [
      'Balcony deck waterproof coating (96 units)',
      'Parking garage deck restoration',
      'Window perimeter resealing',
      'Full exterior repainting',
      'Drain and scupper clearing',
      'Post-project waterproof inspection',
    ],
    stats: [
      { label: 'Duration', value: '14 Weeks' },
      { label: 'Stories', value: '12' },
      { label: 'Balconies', value: '96' },
      { label: 'Completion', value: 'Q4 2024' },
    ],
  },
  {
    id: 4,
    title: 'Luxury Kitchen & Master Bath Remodel',
    category: 'Residential Remodeling',
    location: 'Alpharetta, GA',
    year: 2025,
    mainImage:
      'https://readdy.ai/api/search-image?query=finished%20luxury%20kitchen%20remodel%20with%20white%20quartz%20island%2C%20two-tone%20custom%20cabinets%20navy%20and%20white%2C%20stainless%20appliances%2C%20warm%20pendant%20lighting%2C%20realistic%20interior%20renovation%20photography%2C%20slight%20natural%20imperfections%20in%20light%2C%20honest%20finished%20result%2C%20no%20people%2C%20warm%20ambient%20tones&width=700&height=500&seq=res-r013&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=luxury%20kitchen%20full%20renovation%20result%2C%20white%20quartz%20countertops%20with%20custom%20cabinetry%2C%20farmhouse%20sink%2C%20gold%20faucet%2C%20subway%20tile%20backsplash%2C%20hardwood%20floors%2C%20realistic%20interior%20photography%2C%20warm%20natural%20window%20light%2C%20lived-in%20feeling%2C%20no%20people&width=900&height=600&seq=res-r014&orientation=landscape',
      'https://readdy.ai/api/search-image?query=master%20bathroom%20renovation%20completed%2C%20freestanding%20soaking%20tub%2C%20large%20walk-in%20glass%20shower%20with%20marble%20tile%2C%20floating%20double%20vanity%2C%20natural%20stone%2C%20warm%20LED%20ambient%20light%2C%20realistic%20residential%20renovation%20photography%2C%20no%20people%2C%20inviting%20atmosphere&width=900&height=600&seq=res-r015&orientation=landscape',
      'https://readdy.ai/api/search-image?query=quartz%20waterfall%20kitchen%20island%20edge%20detail%2C%20warm%20pendant%20light%20above%2C%20realistic%20close-up%20renovation%20photography%2C%20slight%20variations%20in%20stone%20veining%2C%20honest%20material%20quality%2C%20no%20people%2C%20intimate%20detail%20shot&width=900&height=600&seq=res-r016&orientation=landscape',
    ],
    description:
      'Complete transformation of the kitchen and master bath in a 4,200 sq ft custom home. The project featured a dramatic open-concept kitchen with a quartz waterfall island and a spa-inspired master bath with a freestanding tub and custom tile shower.',
    scope: [
      'Full kitchen demo & rebuild',
      'Custom cabinetry fabrication',
      'Quartz countertop & waterfall island',
      'Master bath gut renovation',
      'Freestanding tub installation',
      'Heated floor & custom tile work',
    ],
    stats: [
      { label: 'Duration', value: '12 Weeks' },
      { label: 'Home Size', value: '4,200 sq ft' },
      { label: 'Project Area', value: '650 sq ft' },
      { label: 'Completion', value: 'Q1 2025' },
    ],
  },
  {
    id: 5,
    title: 'Open-Concept Living Transformation',
    category: 'Residential Remodeling',
    location: 'Sandy Springs, GA',
    year: 2024,
    mainImage:
      'https://readdy.ai/api/search-image?query=open%20concept%20living%20room%20after%20wall%20removal%20renovation%2C%20combined%20kitchen%20dining%20living%20space%2C%20new%20oak%20hardwood%20floors%2C%20warm%20afternoon%20sunlight%20through%20windows%2C%20realistic%20residential%20interior%20photography%2C%20slight%20imperfections%20in%20light%20giving%20authenticity%2C%20no%20people%2C%20warm%20tones&width=700&height=500&seq=res-r017&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=residential%20open%20plan%20renovation%20result%2C%20load-bearing%20beam%20exposed%2C%20new%20hardwood%20floors%2C%20neutral%20painted%20walls%2C%20recessed%20lights%2C%20real%20interior%20photography%20with%20natural%20warm%20light%2C%20lived-in%20feeling%2C%20honest%20finished%20renovation%2C%20no%20people&width=900&height=600&seq=res-r018&orientation=landscape',
      'https://readdy.ai/api/search-image?query=wide%20plank%20hardwood%20floor%20installation%20completed%20in%20home%2C%20transition%20between%20two%20rooms%2C%20warm%20light%20from%20nearby%20window%2C%20close%20detail%20realistic%20flooring%20photography%2C%20slight%20natural%20wood%20grain%20variation%2C%20honest%20material%20quality%2C%20no%20people&width=900&height=600&seq=res-r019&orientation=landscape',
      'https://readdy.ai/api/search-image?query=residential%20coffered%20ceiling%20with%20recessed%20LED%20lighting%2C%20even%20warm%20illumination%20throughout%20living%20space%2C%20painted%20white%20finish%2C%20realistic%20interior%20photography%2C%20natural%20imperfection%20in%20shadows%20giving%20depth%2C%20no%20people&width=900&height=600&seq=res-r020&orientation=landscape',
    ],
    description:
      'Structural wall removal and full-room transformation to create an open-concept floor plan. The project required engineered beam installation, new flooring throughout, recessed lighting, and complete repainting — delivering a bright, airy living space.',
    scope: [
      'Load-bearing wall removal & beam',
      'Engineered hardwood flooring',
      'Recessed LED lighting install',
      'Full interior repainting',
      'Baseboard & trim replacement',
      'Electrical panel upgrade',
    ],
    stats: [
      { label: 'Duration', value: '7 Weeks' },
      { label: 'Area', value: '1,800 sq ft' },
      { label: 'Walls Removed', value: '3' },
      { label: 'Completion', value: 'Q2 2024' },
    ],
  },
  {
    id: 6,
    title: 'Basement to Entertainment Suite',
    category: 'Residential Remodeling',
    location: 'Roswell, GA',
    year: 2024,
    mainImage:
      'https://readdy.ai/api/search-image?query=finished%20basement%20entertainment%20room%20with%20dark%20painted%20walls%2C%20recessed%20lighting%2C%20LVP%20flooring%2C%20sectional%20sofa%20area%2C%20real%20residential%20interior%20photography%2C%20warm%20moody%20ambiance%2C%20natural%20imperfections%20in%20lighting%2C%20honest%20renovation%20result%2C%20no%20people&width=700&height=500&seq=res-r021&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=basement%20home%20theater%20room%20renovation%2C%20tiered%20seating%2C%20screen%20wall%2C%20acoustic%20panels%2C%20deep%20moody%20dark%20walls%2C%20ambient%20LED%20strip%20lighting%2C%20realistic%20interior%20photography%2C%20warm%20cinema%20atmosphere%2C%20no%20people&width=900&height=600&seq=res-r022&orientation=landscape',
      'https://readdy.ai/api/search-image?query=basement%20wet%20bar%20renovation%20complete%2C%20granite%20counter%2C%20wine%20cooler%2C%20pendant%20lights%2C%20shiplap%20accent%20wall%2C%20realistic%20interior%20renovation%20photography%2C%20warm%20intimate%20lighting%2C%20honest%20material%20finish%2C%20no%20people&width=900&height=600&seq=res-r023&orientation=landscape',
      'https://readdy.ai/api/search-image?query=basement%20full%20bathroom%20addition%20renovation%2C%20subway%20tile%20shower%2C%20modern%20floating%20vanity%2C%20black%20fixtures%2C%20clean%20white%20tile%2C%20realistic%20residential%20bathroom%20photography%2C%20natural%20overhead%20lighting%2C%20no%20people&width=900&height=600&seq=res-r024&orientation=landscape',
    ],
    description:
      'Full basement finishing transforming 1,400 sq ft of unfinished space into a luxury entertainment suite with a dedicated home theater, custom wet bar, and full bath addition. Every detail was designed for premium home entertainment.',
    scope: [
      'Full basement framing & insulation',
      'Home theater room with acoustics',
      'Custom wet bar build',
      'Full bathroom addition',
      'LVP flooring throughout',
      'Dedicated electrical & lighting',
    ],
    stats: [
      { label: 'Duration', value: '14 Weeks' },
      { label: 'Area', value: '1,400 sq ft' },
      { label: 'Rooms Added', value: '4' },
      { label: 'Completion', value: 'Q4 2024' },
    ],
  },
  {
    id: 7,
    title: 'Modern Office Build-Out — Tech Startup',
    category: 'Commercial Remodeling',
    location: 'Atlanta, GA',
    year: 2025,
    mainImage:
      'https://readdy.ai/api/search-image?query=tech%20startup%20office%20interior%20build-out%20complete%2C%20open%20workspace%20with%20exposed%20ceiling%20ductwork%2C%20polished%20concrete%20floors%2C%20glass%20partition%20walls%2C%20honest%20realistic%20commercial%20interior%20photography%2C%20natural%20light%20from%20windows%2C%20warm%20tone%2C%20no%20people%2C%20slight%20imperfections%20giving%20authenticity&width=700&height=500&seq=com-r025&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=modern%20office%20open%20plan%20workspace%20with%20white%20desks%2C%20exposed%20black%20industrial%20ceiling%2C%20glass%20wall%20partitions%2C%20pendant%20lights%2C%20realistic%20commercial%20interior%20photography%2C%20warm%20natural%20light%2C%20no%20people%2C%20honest%20finished%20build-out&width=900&height=600&seq=com-r026&orientation=landscape',
      'https://readdy.ai/api/search-image?query=corporate%20conference%20room%20glass%20wall%20partition%2C%20executive%20table%20and%20chairs%2C%20motorized%20roller%20shade%20on%20window%2C%20realistic%20commercial%20interior%20renovation%20photography%2C%20honest%20material%20quality%2C%20no%20people%2C%20afternoon%20light&width=900&height=600&seq=com-r027&orientation=landscape',
      'https://readdy.ai/api/search-image?query=commercial%20office%20break%20room%20renovation%2C%20custom%20kitchen%20cabinets%2C%20quartz%20counter%2C%20island%20seating%2C%20warm%20pendant%20lighting%2C%20realistic%20interior%20photography%2C%20honest%20commercial%20renovation%20result%2C%20no%20people&width=900&height=600&seq=com-r028&orientation=landscape',
    ],
    description:
      '6,500 sq ft commercial tenant improvement for a fast-growing tech company in Midtown Atlanta. The build-out featured an open-plan workspace, multiple glass conference rooms, a custom break room, and full IT infrastructure — delivered 2 weeks ahead of schedule.',
    scope: [
      'Full demo & open plan buildout',
      'Glass partition system install',
      'Custom millwork & cabinetry',
      'Commercial electrical & data',
      'HVAC zoning & controls',
      'ADA-compliant restroom upgrade',
    ],
    stats: [
      { label: 'Duration', value: '16 Weeks' },
      { label: 'Area', value: '6,500 sq ft' },
      { label: 'Workstations', value: '60+' },
      { label: 'Completion', value: 'Q1 2025' },
    ],
  },
  {
    id: 8,
    title: 'Boutique Retail Space Renovation',
    category: 'Commercial Remodeling',
    location: 'Buckhead, GA',
    year: 2024,
    mainImage:
      'https://readdy.ai/api/search-image?query=upscale%20boutique%20retail%20store%20interior%20renovation%20complete%2C%20display%20racks%20and%20shelving%2C%20polished%20tile%20floor%2C%20track%20lighting%2C%20realistic%20commercial%20interior%20photography%2C%20slight%20warm%20ambient%20light%20creating%20atmosphere%2C%20honest%20renovation%20result%2C%20no%20people&width=700&height=500&seq=com-r029&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=luxury%20boutique%20custom%20display%20wall%20shelving%2C%20accent%20lighting%2C%20white%20and%20warm%20gold%20palette%2C%20realistic%20commercial%20renovation%20photography%2C%20honest%20millwork%20finish%20quality%2C%20slight%20natural%20lighting%2C%20no%20people&width=900&height=600&seq=com-r030&orientation=landscape',
      'https://readdy.ai/api/search-image?query=boutique%20fitting%20room%20renovation%2C%20upholstered%20bench%2C%20full%20length%20mirror%2C%20warm%20flattering%20light%2C%20wallpaper%20accent%20wall%2C%20realistic%20commercial%20interior%20photography%2C%20intimate%20space%2C%20no%20people&width=900&height=600&seq=com-r031&orientation=landscape',
      'https://readdy.ai/api/search-image?query=retail%20glass%20display%20case%20installation%2C%20undercounter%20LED%20lighting%2C%20custom%20millwork%20counter%2C%20realistic%20retail%20renovation%20photography%2C%20honest%20commercial%20finish%2C%20no%20people%2C%20warm%20light&width=900&height=600&seq=com-r032&orientation=landscape',
    ],
    description:
      'Complete retail transformation for an upscale fashion boutique in Buckhead. The renovation included custom built-in display walls, luxury fitting rooms, new flooring, and a complete lighting redesign to showcase merchandise in the best light.',
    scope: [
      'Full interior demo & rebuild',
      'Custom display wall millwork',
      'Luxury fitting room construction',
      'Polished tile flooring',
      'Track & accent lighting design',
      'Glass storefront upgrade',
    ],
    stats: [
      { label: 'Duration', value: '9 Weeks' },
      { label: 'Area', value: '1,800 sq ft' },
      { label: 'Fitting Rooms', value: '4' },
      { label: 'Completion', value: 'Q3 2024' },
    ],
  },
  {
    id: 9,
    title: 'Restaurant & Cafe Full Renovation',
    category: 'Commercial Remodeling',
    location: 'Smyrna, GA',
    year: 2023,
    mainImage:
      'https://readdy.ai/api/search-image?query=restaurant%20dining%20room%20renovation%20complete%2C%20exposed%20brick%20wall%2C%20warm%20pendant%20lights%2C%20wooden%20tables%20and%20chairs%2C%20bar%20seating%20area%2C%20realistic%20interior%20restaurant%20photography%2C%20warm%20intimate%20evening%20atmosphere%2C%20honest%20commercial%20renovation%20result%2C%20no%20people&width=700&height=500&seq=com-r033&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=restaurant%20custom%20banquette%20seating%20renovation%2C%20reclaimed%20wood%20tables%2C%20Edison%20bulb%20pendants%2C%20exposed%20brick%20wall%2C%20realistic%20warm%20restaurant%20interior%20photography%2C%20honest%20commercial%20renovation%2C%20no%20people&width=900&height=600&seq=com-r034&orientation=landscape',
      'https://readdy.ai/api/search-image?query=commercial%20kitchen%20renovation%20stainless%20steel%20surfaces%2C%20line%20cooking%20equipment%2C%20tile%20walls%2C%20commercial%20exhaust%20hood%2C%20realistic%20kitchen%20documentation%20photography%2C%20honest%20commercial%20kitchen%20build%2C%20no%20people&width=900&height=600&seq=com-r035&orientation=landscape',
      'https://readdy.ai/api/search-image?query=cafe%20bar%20counter%20renovation%20with%20espresso%20machine%20area%2C%20custom%20wood%20bar%20top%2C%20open%20shelving%20glassware%2C%20pendant%20lighting%2C%20realistic%20hospitality%20interior%20photography%2C%20warm%20tone%2C%20honest%20renovation%2C%20no%20people&width=900&height=600&seq=com-r036&orientation=landscape',
    ],
    description:
      'Ground-up renovation of a 2,200 sq ft restaurant space including a full commercial kitchen build-out, custom dining room, and a coffee & bar counter. The project navigated strict health code compliance and was delivered on budget within 11 weeks.',
    scope: [
      'Commercial kitchen build-out',
      'Custom dining room millwork',
      'Bar & coffee counter fabrication',
      'Health code compliance upgrades',
      'New HVAC & hood system',
      'Full electrical & plumbing',
    ],
    stats: [
      { label: 'Duration', value: '11 Weeks' },
      { label: 'Area', value: '2,200 sq ft' },
      { label: 'Seating', value: '65 Covers' },
      { label: 'Completion', value: 'Q3 2023' },
    ],
  },
  {
    id: 10,
    title: 'Townhome Full Interior Renovation',
    category: 'Residential Remodeling',
    location: 'Tucker, GA',
    year: 2023,
    mainImage:
      'https://readdy.ai/api/search-image?query=townhome%20interior%20renovation%20complete%2C%20bright%20white%20kitchen%20with%20new%20cabinets%20and%20quartz%20counters%2C%20LVP%20flooring%2C%20updated%20staircase%20with%20iron%20balusters%2C%20realistic%20residential%20interior%20photography%2C%20afternoon%20window%20light%2C%20honest%20home%20renovation%20result%2C%20no%20people&width=700&height=500&seq=res-r037&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=townhome%20kitchen%20renovation%20with%20white%20shaker%20cabinets%2C%20quartz%20counters%2C%20tile%20backsplash%2C%20stainless%20appliances%2C%20realistic%20residential%20kitchen%20photography%2C%20warm%20natural%20light%2C%20honest%20renovation%20finish%2C%20no%20people&width=900&height=600&seq=res-r038&orientation=landscape',
      'https://readdy.ai/api/search-image?query=residential%20staircase%20renovation%20result%2C%20white%20painted%20risers%2C%20new%20oak%20stair%20treads%2C%20black%20iron%20balusters%2C%20freshly%20painted%20hallway%20wall%2C%20realistic%20home%20renovation%20photography%2C%20warm%20natural%20light%2C%20no%20people&width=900&height=600&seq=res-r039&orientation=landscape',
      'https://readdy.ai/api/search-image?query=townhome%20bathroom%20renovation%2C%20new%20vanity%20cabinet%2C%20white%20subway%20tile%20shower%2C%20updated%20tile%20floor%2C%20realistic%20residential%20bathroom%20photography%2C%20clean%20bright%20natural%20light%2C%20honest%20renovation%20result%2C%20no%20people&width=900&height=600&seq=res-r040&orientation=landscape',
    ],
    description:
      'Top-to-bottom renovation of a 1,600 sq ft townhome preparing the property for resale. All three levels were updated including kitchen, all bathrooms, flooring, stairs, and complete interior painting — resulting in a $45K increase in appraised value.',
    scope: [
      'Full kitchen renovation',
      'All bathroom remodels (3)',
      'LVP flooring throughout',
      'Staircase rebuild & iron railing',
      'Interior paint & trim replacement',
      'New light fixtures & hardware',
    ],
    stats: [
      { label: 'Duration', value: '10 Weeks' },
      { label: 'Area', value: '1,600 sq ft' },
      { label: 'Value Added', value: '$45K+' },
      { label: 'Completion', value: 'Q2 2023' },
    ],
  },
  {
    id: 11,
    title: 'Multi-Unit HVAC System Overhaul',
    category: 'General Maintenance',
    location: 'Norcross, GA',
    year: 2024,
    mainImage:
      'https://readdy.ai/api/search-image?query=apartment%20building%20rooftop%20with%20multiple%20new%20HVAC%20condensing%20units%20installed%2C%20neat%20refrigerant%20line%20sets%2C%20organized%20mechanical%20equipment%2C%20realistic%20rooftop%20maintenance%20documentation%20photography%2C%20overcast%20Atlanta%20sky%2C%20honest%20commercial%20HVAC%20project%20photo%2C%20no%20people&width=700&height=500&seq=maint-r041&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=new%20HVAC%20condensing%20units%20on%20rooftop%20in%20a%20row%2C%20fresh%20refrigerant%20line%20sets%20neatly%20organized%2C%20realistic%20mechanical%20documentation%20photography%2C%20slightly%20overcast%20sky%2C%20honest%20commercial%20maintenance%20project%2C%20no%20people&width=900&height=600&seq=maint-r042&orientation=landscape',
      'https://readdy.ai/api/search-image?query=HVAC%20flexible%20ductwork%20connection%20inside%20apartment%20ceiling%20space%2C%20new%20insulated%20flex%20duct%2C%20realistic%20mechanical%20installation%20documentation%2C%20tight%20space%20interior%20photography%2C%20honest%20construction%20photo%2C%20no%20people&width=900&height=600&seq=maint-r043&orientation=landscape',
      'https://readdy.ai/api/search-image?query=new%20smart%20thermostat%20installed%20on%20apartment%20wall%2C%20modern%20digital%20display%2C%20clean%20installation%20with%20fresh%20painted%20wall%2C%20realistic%20residential%20maintenance%20upgrade%20photography%2C%20natural%20interior%20light%2C%20no%20people&width=900&height=600&seq=maint-r044&orientation=landscape',
    ],
    description:
      'Complete replacement of 48 HVAC units across a multi-family apartment complex. The project included all-new rooftop condensing units, air handlers, ductwork connections, and smart thermostat installation — completing 6 units per day without displacing residents.',
    scope: [
      '48 HVAC unit replacements',
      'Rooftop condenser installation',
      'Air handler replacements',
      'Ductwork connections & sealing',
      'Smart thermostat upgrades',
      'System commissioning & testing',
    ],
    stats: [
      { label: 'Duration', value: '3 Weeks' },
      { label: 'Units', value: '48' },
      { label: 'Buildings', value: '6' },
      { label: 'Completion', value: 'Q2 2024' },
    ],
  },
  {
    id: 12,
    title: 'Commercial Grounds Transformation',
    category: 'General Maintenance',
    location: 'Kennesaw, GA',
    year: 2023,
    mainImage:
      'https://readdy.ai/api/search-image?query=commercial%20office%20park%20grounds%20landscaping%20complete%2C%20manicured%20lawn%2C%20trimmed%20hedges%2C%20mulched%20flower%20beds%20with%20seasonal%20plants%2C%20realistic%20documentary%20outdoor%20photography%2C%20natural%20daylight%2C%20honest%20landscape%20project%20result%2C%20no%20people&width=700&height=500&seq=maint-r045&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=commercial%20building%20entrance%20landscaping%20with%20planted%20shrubs%2C%20ornamental%20grasses%2C%20mulched%20beds%2C%20clean%20walkway%20edge%2C%20realistic%20landscape%20documentation%20photography%2C%20natural%20daylight%2C%20honest%20curb%20appeal%20result%2C%20no%20people&width=900&height=600&seq=maint-r046&orientation=landscape',
      'https://readdy.ai/api/search-image?query=commercial%20irrigation%20sprinkler%20heads%20in%20green%20lawn%2C%20running%20water%20spray%20visible%2C%20real%20grounds%20maintenance%20documentation%20photography%2C%20overcast%20light%2C%20honest%20irrigation%20system%20project%2C%20no%20people&width=900&height=600&seq=maint-r047&orientation=landscape',
      'https://readdy.ai/api/search-image?query=commercial%20property%20pathway%20with%20landscape%20bollard%20lights%20at%20dusk%2C%20ambient%20warm%20glow%2C%20realistic%20exterior%20lighting%20installation%20photography%2C%20honest%20commercial%20grounds%20project%20result%2C%20no%20people&width=900&height=600&seq=maint-r048&orientation=landscape',
    ],
    description:
      'Full grounds transformation for a 4-building commercial office park including new landscape design, irrigation system installation, exterior lighting, and quarterly maintenance contract. The property\'s curb appeal received major recognition from tenants and management alike.',
    scope: [
      'New landscape design & install',
      'Drip & sprinkler irrigation system',
      'Exterior pathway lighting',
      'Tree trimming & removal',
      'Mulching & seasonal color',
      'Ongoing quarterly maintenance',
    ],
    stats: [
      { label: 'Duration', value: '4 Weeks' },
      { label: 'Area', value: '6.5 Acres' },
      { label: 'Buildings', value: '4' },
      { label: 'Completion', value: 'Q3 2023' },
    ],
  },
];

export const CATEGORIES: ProjectCategory[] = [
  'Building & Facility Maintenance',
  'Residential Remodeling',
  'Commercial Remodeling',
  'General Maintenance',
];
